import React from 'react';
import { useFullTheme } from '~/design';
import { InternalText } from '~/internal/InternalText';

const ListContext = React.createContext<true | null>(null);

export interface ListProps {
  /**
   * Use this prop to provide an accessible name to the list
   */
  'aria-label'?: string;

  /**
   * Use this prop to link an element with an accessible name located outside to the list
   */
  'aria-labelledby'?: string;

  /**
   * The content of the list
   */
  children: React.ReactElement<ListProps> | Array<React.ReactElement<ListProps>>;

  /**
   * Whether the list should be ordered or not (default: `false`)
   * - To determine which one to use, try changing the order of the list items.
   * - If the meaning is changed, you should set this `true`.
   */
  ordered?: boolean;
}

/**
 * A list is used to display list items.
 * - Unordered list items are marked with bullets.
 * - Ordered list items (`ordered=true`) are marked with numbers.
 */
export function List({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  ordered = false,
}: ListProps) {
  const theme = useFullTheme();

  const Tag = ordered ? 'ol' : 'ul';

  return (
    <ListContext.Provider value>
      <Tag
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing[2],
          marginBlock: 0,
          marginInline: 0,
          paddingLeft: theme.spacing[5],
        }}
      >
        {children}
      </Tag>
    </ListContext.Provider>
  );
}

export interface ListItemProps {
  /**
   * Use this prop to provide an accessible name to the list item
   */
  'aria-label'?: string;
  children: React.ReactNode;
}

List.Item = function ListItem({ 'aria-label': ariaLabel, children }: ListItemProps) {
  if (!React.useContext(ListContext)) {
    throw new Error('List.Item can only be rendered inside List');
  }

  return (
    <InternalText as="li" aria-label={ariaLabel}>
      {children}
    </InternalText>
  );
};
